/* ============================================================================
   recommendations.js - lazy product-recommendation loader.

   Hydrates [data-recommendations][data-url] placeholders by fetching the
   Product Recommendations Section Rendering API (which is what populates the
   Liquid `recommendations` object) and swapping in the rendered products. One
   mechanism serves every surface:
     - PDP  : a static section (sections/product-recommendations.liquid) placed
              in templates/product.json; renders empty on first paint, then this
              loader fetches the populated version. Lazy (IntersectionObserver).
     - Cart : a placeholder inside #cart-drawer-root (the drawer). cart.js
              re-renders that root (replaceWith) on every mutation, so a
              MutationObserver re-hydrates the fresh placeholder each time. The
              loader hydrates any [data-recommendations][data-url] node, so the
              same mechanism would cover #main-cart-root if a placeholder were
              added to sections/main-cart.liquid (none ships today).

   DOM contract:
     [data-recommendations]  a placeholder block
       data-url              recommendations endpoint URL to fetch
                             (routes.product_recommendations_url + section_id,
                              product_id, intent, limit)
       data-rec-lazy         present -> defer the fetch until near the viewport
   The fetched response is a rendered section whose root is also
   [data-recommendations]; its innerHTML (heading + cards) is copied in. An empty
   result hides the block so no stray heading shows.
   ========================================================================== */
(function () {
  "use strict";

  // The theme's one seam for the globals an asset cannot fake for itself. See
  // the block at the top of assets/cart.js for why it is a global rather than an
  // argument. This file needs exactly one thing from it: the fetch, so a test
  // can answer with a rendered section instead of reaching the network.
  //
  // The IntersectionObserver below is deliberately NOT in there. jsdom does not
  // implement one either, but it is a standard browser global, so a test can
  // define it on the window and this file reads the fake without being asked to
  // carry a hook for it. A seam with one adapter that works without the seam is
  // a hook to keep in step with nothing.
  var env = window.DCEnv || {};
  var http = env.fetch || function (u, o) { return window.fetch(u, o); };

  // url -> Promise<string> of the section's inner HTML. Dedupes the repeat
  // fetches that cart re-renders would otherwise fire for the same seed product.
  var cache = {};

  function fetchInner(url) {
    if (cache[url]) return cache[url];
    var p = http(url, { headers: { "X-Requested-With": "XMLHttpRequest" } })
      .then(function (res) {
        if (!res.ok) throw new Error("recommendations fetch failed: " + res.status);
        return res.text();
      })
      .then(function (html) {
        if (!html) return "";
        var doc = new DOMParser().parseFromString(html, "text/html");
        var found = doc.querySelector("[data-recommendations]");
        return found ? found.innerHTML.trim() : "";
      })
      .catch(function () {
        // Transient failure (network error / non-2xx): drop the cache entry so a
        // later re-render can retry, instead of pinning this URL to "empty" for
        // the whole session. A real 200-with-no-products resolves to "" above and
        // stays cached, so genuinely empty results are never re-fetched.
        delete cache[url];
        return "";
      });
    cache[url] = p;
    return p;
  }

  function hydrate(elm) {
    if (!elm || elm.dataset.recState) return; // once per element
    var url = elm.getAttribute("data-url");
    if (!url) return;
    elm.dataset.recState = "pending";

    function load() {
      fetchInner(url).then(function (inner) {
        if (inner) {
          elm.innerHTML = inner;
          elm.dataset.recState = "done";
        } else {
          elm.dataset.recState = "empty";
          elm.setAttribute("hidden", "");
        }
      });
    }

    if (elm.hasAttribute("data-rec-lazy") && "IntersectionObserver" in window) {
      var io = new window.IntersectionObserver(function (entries, obs) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { obs.disconnect(); load(); }
        });
      }, { rootMargin: "400px 0px" });
      io.observe(elm);
    } else {
      load();
    }
  }

  function scan(root) {
    (root || document).querySelectorAll("[data-recommendations][data-url]").forEach(hydrate);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { scan(); });
  } else {
    scan();
  }

  // Cart sections are re-rendered by cart.js; re-hydrate fresh placeholders.
  if ("MutationObserver" in window) {
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) {
          if (n.nodeType !== 1) return;
          if (n.matches && n.matches("[data-recommendations][data-url]")) hydrate(n);
          if (n.querySelectorAll) scan(n);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
